import { useMemo, useState } from 'react';
import { useStore } from '@/state/store';
import {
  ASSET_STRESS_PRESETS,
  buildInitialStates,
  combinedCapGainsRate,
  stressSnapshot,
  type AssetStressPreset,
  type BucketState,
} from '@/engine';
import { fmtCurrency, fmtRatioPct } from '@/lib/format';
import type { AssetClass } from '@/types';
import { TOOLTIPS } from '@/content/tooltips';
import { CHART_HELP } from '@/content/chartHelp';
import { InfoTooltip } from './InfoTooltip';
import { HowToRead } from './HowToRead';

const PRESETS: { value: AssetStressPreset; label: string }[] = [
  { value: 'crash_2008', label: '2008 crash' },
  { value: 'equity_bear', label: 'Equity bear' },
  { value: 'rate_spike_assets', label: 'Rate spike' },
];

/** Interactive stress test backed by the pure, non-destructive stressSnapshot (§6.23, §10.11). */
export function StressTestPanel() {
  const inputs = useStore((s) => s.inputs);
  const result = useStore((s) => s.result);
  const setInputs = useStore((s) => s.setInputs);
  const months = inputs.years * 12;

  const [atMonth, setAtMonth] = useState(0);
  const [globalDrop, setGlobalDrop] = useState(0);
  const [dropByClass, setDropByClass] = useState<Partial<Record<AssetClass, number>>>({});

  // Bucket states at the selected month: scale the initial composition by projected growth.
  const { buckets, balance } = useMemo(() => {
    const initial = buildInitialStates(inputs);
    const row = result?.rows[Math.min(atMonth, months - 1)];
    const initialPv = initial.reduce((s, b) => s + b.value, 0);
    const ratio = row && initialPv > 0 ? row.portfolioValue / initialPv : 1;
    const scaled: BucketState[] = initial.map((b) => ({ ...b, value: b.value * ratio, basis: b.basis * ratio }));
    return { buckets: scaled, balance: row ? row.endBalance : inputs.initialBalance };
  }, [inputs, result, atMonth, months]);

  const snapshot = useMemo(
    () =>
      stressSnapshot({
        buckets,
        balance,
        atMonth,
        minimumLVC: inputs.minimumLoanValue,
        combinedRate: combinedCapGainsRate(inputs.tax),
        cureTargetLtv: inputs.cureTargetLtv,
        policy: inputs.liquidationPolicy,
        globalDropPct: inputs.useBuckets ? undefined : globalDrop,
        dropByClass: inputs.useBuckets ? dropByClass : undefined,
      }),
    [buckets, balance, atMonth, inputs, globalDrop, dropByClass],
  );
  const called = snapshot.maintenanceCall;

  const applyAsShock = () => {
    if (inputs.useBuckets) {
      // Approximate per-class drops as a single aggregate shock at the month.
      const avg = buckets.length ? buckets.reduce((s, b) => s + (dropByClass[b.assetClass] ?? 0), 0) / buckets.length : 0;
      setInputs({ collateralShocks: [...inputs.collateralShocks, { monthIndex: atMonth, pctChange: -avg }] });
    } else {
      setInputs({ collateralShocks: [...inputs.collateralShocks, { monthIndex: atMonth, pctChange: -globalDrop }] });
    }
  };

  const uniqueClasses = [...new Set(buckets.map((b) => b.assetClass))];

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Stress test<InfoTooltip text={TOOLTIPS.stressSlider} label="Stress test" /></h3>
        <span className={`rounded px-2 py-0.5 text-xs font-bold ${called ? 'bg-danger text-white' : 'bg-safe text-white'}`}>
          {called ? 'CALL' : 'SAFE'}
        </span>
      </div>

      <label className="mt-2 flex items-center justify-between text-xs">
        At month {atMonth}
        <input type="range" min={0} max={months - 1} value={atMonth} onChange={(e) => setAtMonth(Number(e.target.value))} className="w-1/2 accent-sky-600" />
      </label>

      {inputs.useBuckets ? (
        <div className="mt-2 flex flex-col gap-1">
          <div className="flex flex-wrap gap-1">
            {PRESETS.map((p) => (
              <button key={p.value} type="button" className="rounded border border-slate-300 px-1.5 py-0.5 text-[11px] hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800" onClick={() => setDropByClass({ ...ASSET_STRESS_PRESETS[p.value] })}>
                {p.label}
              </button>
            ))}
          </div>
          {uniqueClasses.map((cls) => (
            <label key={cls} className="flex items-center justify-between text-[11px]">
              {cls} {dropByClass[cls] ?? 0}%
              <input type="range" min={-10} max={80} value={dropByClass[cls] ?? 0} onChange={(e) => setDropByClass({ ...dropByClass, [cls]: Number(e.target.value) })} className="w-1/2 accent-danger" />
            </label>
          ))}
        </div>
      ) : (
        <label className="mt-2 flex items-center justify-between text-xs">
          Market drop {globalDrop}%
          <input type="range" min={0} max={80} value={globalDrop} onChange={(e) => setGlobalDrop(Number(e.target.value))} className="w-1/2 accent-danger" />
        </label>
      )}

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div><dt className="text-slate-500">Stressed portfolio</dt><dd className="font-semibold tabular-nums">{fmtCurrency(snapshot.stressedPortfolio)}</dd></div>
        <div><dt className="text-slate-500">Stressed LVC</dt><dd className="font-semibold tabular-nums">{fmtCurrency(snapshot.stressedLVC)}</dd></div>
        <div><dt className="text-slate-500">LTV</dt><dd className="font-semibold tabular-nums">{fmtRatioPct(snapshot.ltv)}</dd></div>
        <div><dt className="text-slate-500">Call cushion</dt><dd className="font-semibold tabular-nums">{fmtRatioPct(snapshot.callCushionPct)}</dd></div>
        <div><dt className="text-slate-500">Margin of safety</dt><dd className="font-semibold tabular-nums">{fmtCurrency(snapshot.marginOfSafety)}</dd></div>
        <div><dt className="text-slate-500">Triggers a call at</dt><dd className="font-semibold tabular-nums">−{snapshot.callThresholdDropPct.toFixed(1)}%</dd></div>
      </dl>

      <p className="mt-2 text-xs text-slate-600 dark:text-slate-300">
        A <strong>{snapshot.callThresholdDropPct.toFixed(0)}%</strong> drop triggers a maintenance call.
        {snapshot.cure && ` Curing it would force ~${fmtCurrency(snapshot.cure.forcedSale)} in sales and ~${fmtCurrency(snapshot.cure.realizedTax)} in realized tax.`}
      </p>

      <button type="button" className="mt-2 rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800" onClick={applyAsShock}>
        Apply as scheduled shock
      </button>
      <HowToRead text={CHART_HELP.stressTest} />
    </div>
  );
}
